import { allDataSequenceIds } from "building-types/dataSequences/dataSequenceTools";
import { isSystemDataSequenceId } from "building-types/dataSequences/isSystemDataSequenceId";
import type { Doc } from "building-types/doc";
import type { Parameter, ParameterValue } from "building-types/options/parameter";
import { ParameterType } from "building-types/options/parameter";
import { viewportCameraKeys } from "building-types/views/CameraKey";
import { builtInPlanSetTemplateIdLabels } from "building-types/views/planSets";
import {
  isBuiltInPlanSetTemplateIdentifier,
  isCustomPlanSetTemplateIdentifier,
} from "building-types/views/planSetTemplateIdentifierTools";
import { BuiltinViewScaleFactors } from "building-types/views/viewScaleConstants";
import type { Table } from "common/data/table";
import { ensureIsString } from "common/utils/assertOfType";
import objectFilter from "common/utils/objectFilter";
import objectFind from "common/utils/objectFind";
import objectKeyCount from "common/utils/objectKeyCount";
import { camelCaseToTitleCase, snakeCaseToTitleCase } from "common/utils/strings";
import { docEntityGetChildEntityIdsFromTable } from "doc/entity/docEntityGetChildrenFromTable";
import { evaluateParameterDefaultValues } from "doc/parameterCoreTools";
import {
  evaluateParameterValueHandles,
  getParameterReplacementValue,
  isParameterRef,
  parameterValuesMerge,
} from "doc/parameterTools";
import { getReportingUnitsWithDefaultNamesFromDoc } from "doc/utils/labelTools";
import { formatViewScaleFactor } from "doc/viewScaleFormat";
import Toggle from "lib/components/actions/Toggle";
import { useDownloadPlanModalContext } from "lib/components/builders/communities/plans/modals/DownloadPlanModalContextProvider";
import computeDefaultPlanSetExportFileName from "lib/components/design/defaultPlanSetExportFileName";
import useFilteredPlanSetTemplatesForExport from "lib/components/design/useFilteredPlanSetTemplatesForExport";
import { useUserContext } from "lib/state/UserContextProvider";
import { canDownloadBuiltInPlanSets } from "lib/utils/planDocumentPermissions";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import Button from "ui/buttons/Button";
import Checkbox from "ui/forms/Checkbox";
import InputLabel from "ui/forms/InputLabel";
import type { SelectBasicItem } from "ui/forms/SimpleSelect";
import SimpleSelect from "ui/forms/SimpleSelect";
import IconInfoLine from "ui/icons/IconInfoLine";
import Tooltip from "ui/popovers/Tooltip";
import styles from "./ExportPlanSetTemplateModalContent.module.scss";

interface ExportPlanSetTemplateModalContentProps {
  doc: Doc;
  userId?: string;
  homePlanSalesAgentId?: string | null;
  locationSalesAgentId?: string | null;
  exportContextName?: string;
  allowBuiltInPlanSetTemplate?: boolean;
}

export default function ExportPlanSetTemplateModalContent({
  doc,
  userId,
  homePlanSalesAgentId,
  locationSalesAgentId,
  exportContextName,
  allowBuiltInPlanSetTemplate = true,
}: ExportPlanSetTemplateModalContentProps) {
  const { enabledFeatures, hasPermission } = useUserContext();
  const {
    planSetTemplateIdentifier,
    setPlanSetTemplateIdentifier,
    parameterValueOverrides,
    setParameterValueOverrides,
    filterDisabledOptionSetOverrides,
    setFilterDisabledOptionSetOverrides,
    exportFileName,
    setExportFileName,
  } = useDownloadPlanModalContext();
  const filteredPlanSetTemplates = useFilteredPlanSetTemplatesForExport(
    doc.planSetTemplates,
    userId,
    homePlanSalesAgentId,
    locationSalesAgentId,
  );
  const [editingParameterValues, setEditingParameterValues] = useState(false);

  const [defaultPlanSetTemplateId] = Object.keys(builtInPlanSetTemplateIdLabels);

  const canDownloadBuiltIn =
    allowBuiltInPlanSetTemplate &&
    canDownloadBuiltInPlanSets({
      hasPermission,
      userId,
      homePlanSalesAgentId,
      locationSalesAgentId,
    });

  const planSetTemplateId = isBuiltInPlanSetTemplateIdentifier(planSetTemplateIdentifier)
    ? defaultPlanSetTemplateId
    : planSetTemplateIdentifier.templateId;

  const planSetTemplateLocalParameters = useMemo(() => {
    if (isBuiltInPlanSetTemplateIdentifier(planSetTemplateIdentifier)) {
      return {};
    }
    return objectFilter(doc.localParameters, (param) => param.parentId === planSetTemplateId);
  }, [doc.localParameters, planSetTemplateId]);

  const planSetTemplateConfigIds = useMemo(
    () =>
      docEntityGetChildEntityIdsFromTable(doc.planSetTemplateConfigs, planSetTemplateIdentifier.templateId ?? "") ?? [],
    [doc.planSetTemplateConfigs, planSetTemplateIdentifier.templateId],
  );

  const planSetTemplateItems: SelectBasicItem[] = useMemo(() => {
    const items: SelectBasicItem[] = filteredPlanSetTemplates.map((template) => ({
      type: "item",
      value: template.id,
      label: template.name,
    }));

    if (canDownloadBuiltIn) {
      items.unshift({
        type: "item",
        value: defaultPlanSetTemplateId,
        label: builtInPlanSetTemplateIdLabels[defaultPlanSetTemplateId],
      });
    }
    return items;
  }, [filteredPlanSetTemplates, canDownloadBuiltIn, defaultPlanSetTemplateId]);

  const hasSelectedPlanSetTemplate = planSetTemplateItems.some((item) => item.value === planSetTemplateId);

  const planSetTemplateConfigItems: SelectBasicItem[] = useMemo(() => {
    if (!enabledFeatures.userDefinedPlansets) {
      return [];
    }

    return planSetTemplateConfigIds.map((config) => ({
      type: "item",
      value: config,
      label: doc.planSetTemplateConfigs[config].name,
    }));
  }, [doc, planSetTemplateConfigIds, enabledFeatures.userDefinedPlansets]);

  const onPlanSetTemplateChange = useCallback(
    (templateId: string | null) => {
      if (typeof templateId !== "string") {
        return;
      }
      setParameterValueOverrides({});
      setExportFileName("");
      setEditingParameterValues(false);
      if (templateId === defaultPlanSetTemplateId) {
        setPlanSetTemplateIdentifier({ type: "BUILT_IN" });
      } else {
        setPlanSetTemplateIdentifier({ type: "CUSTOM", templateId });
      }
    },
    [defaultPlanSetTemplateId, setExportFileName, setPlanSetTemplateIdentifier, setParameterValueOverrides],
  );

  useEffect(() => {
    if (!planSetTemplateItems.length) {
      return;
    }

    if (!hasSelectedPlanSetTemplate) {
      onPlanSetTemplateChange(planSetTemplateItems[0].value);
    }
  }, [planSetTemplateItems, hasSelectedPlanSetTemplate, onPlanSetTemplateChange]);

  const planSetTemplateConfig = useMemo(() => {
    const specifiedConfig = doc.planSetTemplateConfigs[planSetTemplateIdentifier.templateConfigId ?? ""];

    if (specifiedConfig) {
      return specifiedConfig;
    }

    // default config
    return objectFind(doc.planSetTemplateConfigs, (config) => config.parentId === planSetTemplateIdentifier.templateId);
  }, [doc.planSetTemplateConfigs, planSetTemplateIdentifier.templateConfigId, planSetTemplateIdentifier.templateId]);

  const { localParameterValues, globalParameterValues } = useMemo(() => {
    if (isBuiltInPlanSetTemplateIdentifier(planSetTemplateIdentifier)) {
      return {
        localParameterValues: {},
        globalParameterValues: {},
      };
    }
    const mergedLocalParameterValues = {
      ...evaluateParameterDefaultValues(planSetTemplateLocalParameters),
    };
    const mergedGlobalParameterValues = {
      ...evaluateParameterDefaultValues(doc.globalParameters),
    };

    if (!planSetTemplateConfig) {
      return {
        localParameterValues: mergedLocalParameterValues,
        globalParameterValues: mergedGlobalParameterValues,
      };
    }

    parameterValuesMerge(
      mergedLocalParameterValues,
      filterParameterValuesByParameters(
        evaluateParameterValueHandles(planSetTemplateConfig.parameterValues),
        planSetTemplateLocalParameters,
      ),
    );
    parameterValuesMerge(
      mergedGlobalParameterValues,
      filterParameterValuesByParameters(
        evaluateParameterValueHandles(planSetTemplateConfig.globalParameterValues),
        doc.globalParameters,
      ),
    );

    return {
      localParameterValues: mergedLocalParameterValues,
      globalParameterValues: mergedGlobalParameterValues,
    };
  }, [doc.globalParameters, planSetTemplateConfig, planSetTemplateLocalParameters]);

  const defaultExportFileName = useMemo(
    () =>
      computeDefaultPlanSetExportFileName({
        doc,
        planSetTemplateIdentifier,
        exportContextName,
      }),
    [doc, planSetTemplateIdentifier, exportContextName],
  );
  const effectiveExportFileName = exportFileName || defaultExportFileName;

  const resolvedParameters = useMemo(() => {
    if (isBuiltInPlanSetTemplateIdentifier(planSetTemplateIdentifier)) {
      return [];
    }

    return [
      ...resolveParameterEntries(localParameterValues, planSetTemplateLocalParameters),
      ...resolveParameterEntries(globalParameterValues, doc.globalParameters),
    ];
  }, [
    localParameterValues,
    globalParameterValues,
    planSetTemplateLocalParameters,
    doc.globalParameters,
    planSetTemplateIdentifier,
  ]);

  const hasOverrides = objectKeyCount(parameterValueOverrides) > 0;

  if (!planSetTemplateItems.length) {
    return null;
  }

  return (
    <div className={styles.body}>
      <div className={styles.templatesSection}>
        <div className={styles.studioDocTitleSection}>
          <InputLabel text="Studio File" />
          <h3 className={styles.studioDocTitle}>{doc.name}</h3>
        </div>
        <InputLabel text="Plan Set Template" />
        <SimpleSelect.Basic
          placeholder="Select template..."
          items={planSetTemplateItems}
          value={planSetTemplateId}
          onValueChange={onPlanSetTemplateChange}
          className={styles.select}
          constrainWidth
        />

        {isCustomPlanSetTemplateIdentifier(planSetTemplateIdentifier) && (
          <>
            <div className={styles.configSelect}>
              <InputLabel text="Instance" />
              <SimpleSelect.Basic
                placeholder="Select configuration..."
                value={planSetTemplateIdentifier.templateConfigId ?? String(planSetTemplateConfigItems[0]?.value)}
                items={planSetTemplateConfigItems}
                className={styles.select}
                constrainWidth
                onValueChange={(templateConfigId) => {
                  setParameterValueOverrides({});
                  setExportFileName("");
                  setEditingParameterValues(false);
                  setPlanSetTemplateIdentifier({
                    type: "CUSTOM",
                    templateId: planSetTemplateId,
                    templateConfigId: ensureIsString(templateConfigId),
                  });
                }}
              />
            </div>

            <div className={styles.toggleRow}>
              <div className={styles.toggleLabelRow}>
                <label className={styles.toggleLabel} htmlFor="hide-disabled-content">
                  Hide Disabled Content
                </label>
                <Tooltip.Root delayDuration={100}>
                  <Tooltip.Trigger>
                    <span className={styles.toggleInfoIcon}>
                      <IconInfoLine size={12} />
                    </span>
                  </Tooltip.Trigger>
                  <Tooltip.Content side="bottom" sideOffset={4}>
                    When enabled, sheet content (viewports, data tables, etc.) associated with disabled Options will be
                    hidden.
                  </Tooltip.Content>
                </Tooltip.Root>
              </div>
              <Toggle
                id="hide-disabled-content"
                isTrue={filterDisabledOptionSetOverrides}
                onChange={setFilterDisabledOptionSetOverrides}
                variants="small"
              />
            </div>

            <div className={styles.saveAsRow}>
              <InputLabel text="Save As" />
              <input
                type="text"
                value={effectiveExportFileName}
                onChange={(e) => setExportFileName(e.target.value)}
                className={styles.parameterTextInput}
              />
            </div>
          </>
        )}
      </div>

      <div className={styles.parameterSection}>
        <div className={styles.parameterCard}>
          <div className={styles.parameterHeader}>
            <div>
              <h3 className={styles.parameterTitle}>Parameter Values</h3>
              <div className={styles.parameterSubtitle}>
                {planSetTemplateConfig?.name}
                {hasOverrides && (
                  <button
                    type="button"
                    className={styles.clearAllOverrides}
                    onClick={() => setParameterValueOverrides({})}
                  >
                    Clear overrides
                  </button>
                )}
              </div>
            </div>
            {resolvedParameters.length > 0 && (
              <Button
                btnStyle="secondary"
                btnSize="medium"
                onClick={() => setEditingParameterValues((prev) => !prev)}
                className={styles.editParameterValuesButton}
              >
                {editingParameterValues ? "Done" : "Edit Parameter Values"}
              </Button>
            )}
          </div>
          <div className={styles.parameterList}>
            {resolvedParameters.length === 0 ? (
              <div className={styles.parameterEmpty}>
                <span>No parameter values configured</span>
                <p className={styles.parametersEmptySubtext}>
                  Parameter values associated with the selected template & configuration instance will appear here.
                </p>
              </div>
            ) : (
              resolvedParameters.map((entry) => {
                const isOverridden = entry.parameter.id in parameterValueOverrides;
                const currentValue = parameterValueOverrides[entry.parameter.id] ?? entry.value;
                return (
                  <div key={entry.parameter.id} className={styles.parameterItem}>
                    <div className={styles.parameterNameRow}>
                      <span className={styles.parameterName}>{entry.parameter.name}</span>
                      {isOverridden && (
                        <>
                          <span className={styles.overrideBadge}>Modified</span>
                          <button
                            type="button"
                            className={styles.clearOverride}
                            onClick={() => {
                              const next = { ...parameterValueOverrides };
                              delete next[entry.parameter.id];
                              setParameterValueOverrides(next);
                            }}
                          >
                            Clear
                          </button>
                        </>
                      )}
                    </div>
                    {editingParameterValues && !isParameterRef(currentValue) ? (
                      <ParameterEditControl
                        parameter={entry.parameter}
                        value={currentValue}
                        doc={doc}
                        onChange={(value) =>
                          setParameterValueOverrides((prev) => ({ ...prev, [entry.parameter.id]: value }))
                        }
                      />
                    ) : (
                      <div className={styles.parameterValue}>
                        {formatPrimitiveValue(entry.parameter, currentValue, doc)}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

interface ResolvedParameterEntry {
  parameter: Parameter;
  value: ParameterValue;
}

function resolveParameterEntries(
  values: Table<ParameterValue>,
  parameters: Table<Parameter>,
): ResolvedParameterEntry[] {
  const entries: ResolvedParameterEntry[] = [];
  for (const [id, parameter] of Object.entries(parameters)) {
    entries.push({ parameter, value: values[id] ?? parameter.defaultValue ?? "" });
  }
  entries.sort((a, b) => a.parameter.name.localeCompare(b.parameter.name));
  return entries;
}

function filterParameterValuesByParameters(
  values: Table<ParameterValue>,
  parameters: Table<Parameter>,
): Table<ParameterValue> {
  const filteredValues: Record<string, ParameterValue> = {};

  for (const [parameterId, value] of Object.entries(values)) {
    if (parameterId in parameters) {
      filteredValues[parameterId] = value;
    }
  }

  return filteredValues;
}

function formatPrimitiveValue(parameter: Parameter, value: ParameterValue, doc: Doc): string {
  const replacement = getParameterReplacementValue(parameter.id, {
    params: { [parameter.id]: parameter },
    values: { [parameter.id]: value },
    doc,
  });

  return replacement ?? String(value);
}

function defaultSelectItemsForTable(table: Table<{ name: string }>): SelectBasicItem[] {
  return Object.entries(table).map(([id, entity]) => ({
    type: "item",
    value: id,
    label: entity.name,
  }));
}

function getParameterSelectItems(parameter: Parameter, doc: Doc): SelectBasicItem[] {
  switch (parameter.type) {
    case ParameterType.PRESET:
      return defaultSelectItemsForTable(doc.presets);
    case ParameterType.LEVEL:
      return defaultSelectItemsForTable({ ...doc.levels, ...doc.compositeLevels });
    case ParameterType.UNIT:
      return getReportingUnitsWithDefaultNamesFromDoc(doc).map((unit) => ({
        type: "item",
        value: unit.id,
        label: unit.name,
      }));
    case ParameterType.ELEVATION_VIEW:
      return defaultSelectItemsForTable(doc.elevationViews);
    case ParameterType.SECTION_VIEW:
      return defaultSelectItemsForTable(doc.sectionViews);
    case ParameterType.DRAWING_TEMPLATE:
      return defaultSelectItemsForTable(doc.contentTemplates);
    case ParameterType.OPTION: {
      const optionSet = doc.optionSets[parameter.optionSetId];
      return defaultSelectItemsForTable(optionSet?.options ?? {});
    }
    case ParameterType.VIEW_TEMPLATE:
      return Object.entries(doc.viewTemplates)
        .filter(([, vt]) => vt?.viewType === parameter.viewType)
        .map(([id, template]) => ({
          type: "item",
          value: id,
          label: template.name,
        }));
    case ParameterType.VIEW_3D:
      return viewportCameraKeys.map((key) => ({
        type: "item",
        value: key,
        label: camelCaseToTitleCase(key),
      }));
    case ParameterType.DRAWING_SCALE:
      return BuiltinViewScaleFactors.map((factor) => ({
        type: "item",
        value: String(factor),
        label: formatViewScaleFactor(String(factor)),
      }));
    case ParameterType.DATA_SEQUENCE:
      return allDataSequenceIds(doc.dataSequences).map((id) => ({
        type: "item",
        value: id,
        label: isSystemDataSequenceId(id) ? snakeCaseToTitleCase(id) : doc.dataSequences[id]?.name ?? id,
      }));
    default:
      return [];
  }
}

interface ParameterEditControlProps {
  parameter: Parameter;
  value: ParameterValue;
  doc: Doc;
  onChange: (value: ParameterValue) => void;
}

const ParameterEditControl = memo(function ParameterEditControl({
  parameter,
  value,
  doc,
  onChange,
}: ParameterEditControlProps) {
  switch (parameter.type) {
    case ParameterType.BOOLEAN: {
      const checked = typeof value === "boolean" ? value : String(value) === "true";
      return <Checkbox checked={checked} onChangeBoolean={onChange} />;
    }
    case ParameterType.TEXT:
    case ParameterType.URL:
      return (
        <input
          type="text"
          value={String(value ?? "")}
          onChange={(e) => onChange(e.target.value)}
          className={styles.parameterTextInput}
        />
      );
    case ParameterType.NUMBER:
    case ParameterType.FOOT_INCH:
      return (
        <input
          type="text"
          value={String(value ?? "")}
          onChange={(e) => onChange(e.target.value)}
          className={styles.parameterTextInput}
        />
      );
    default: {
      const items = getParameterSelectItems(parameter, doc);
      if (!items.length) {
        return (
          <input
            type="text"
            value={String(value ?? "")}
            onChange={(e) => onChange(e.target.value)}
            className={styles.parameterTextInput}
          />
        );
      }
      return (
        <SimpleSelect.Basic
          items={items}
          value={String(value)}
          onValueChange={(v) => {
            if (v !== null) {
              onChange(v);
            }
          }}
          className={styles.parameterSelect}
          constrainWidth
        />
      );
    }
  }
});
